import { useCallback, useEffect } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { useKeyPress } from "react-use";
import { rotatePoints, scalePolygon } from "../math";
import { Shape } from "../RampCanvas";
import { updateAtId } from "../utils";

type Props = {
  shapes: Shape[];
  setShapes: React.Dispatch<React.SetStateAction<Shape[]>>;
  activeShapes: string[];
  setActiveShapes: React.Dispatch<React.SetStateAction<string[]>>;
  pixelsToFeetRatio: number;
  getActiveShapes: () => Shape[];
  undo: () => void;
  duplicateShapes: () => void;
};
export const useCanvasHotkeys = (props: Props) => {
  const {
    shapes,
    setShapes,
    activeShapes,
    setActiveShapes,
    getActiveShapes,
    pixelsToFeetRatio,
    undo,
    duplicateShapes,
  } = props;
  /**
   * Rotate the shape we're hover over when shift + <arrow> is pressed
   */
  const [isShiftPressed] = useKeyPress("Shift");
  const [isRightArrowPressed] = useKeyPress("ArrowRight");
  const [isLeftArrowPressed] = useKeyPress("ArrowLeft");
  const [isUpArrowPressed] = useKeyPress("ArrowUp");
  const [isDownArrowPressed] = useKeyPress("ArrowDown");

  // Extract movement logic into a memoized callback
  const performMovement = useCallback(() => {
    if (activeShapes.length === 0) return;

    // Rotation with Shift + Left/Right
    if (isShiftPressed && (isRightArrowPressed || isLeftArrowPressed)) {
      setShapes((currentShapes) => {
        const selectedShapes = currentShapes.filter((s) =>
          activeShapes.includes(s.id)
        );

        // T-shape snapping logic
        if (
          (selectedShapes.every((shape) => shape.type === "Tie Down T") ||
            selectedShapes.every((shape) => shape.type === "Double T")) &&
          !selectedShapes
            .map((shape) =>
              Math.round(
                100 *
                  Math.atan2(
                    shape.points[1].y - shape.points[0].y,
                    shape.points[1].x - shape.points[0].x
                  )
              )
            )
            .every((val, i, arr) => val === arr[0])
        ) {
          const referenceAngle = Math.atan2(
            selectedShapes[0].points[1].y - selectedShapes[0].points[0].y,
            selectedShapes[0].points[1].x - selectedShapes[0].points[0].x
          );

          let updatedShapes = [...currentShapes];
          selectedShapes.forEach((shape) => {
            const currentAngle = Math.atan2(
              shape.points[1].y - shape.points[0].y,
              shape.points[1].x - shape.points[0].x
            );
            const angleDifference = referenceAngle - currentAngle;
            const rotatedPoints = rotatePoints(
              shape.points,
              (angleDifference * 180) / Math.PI
            );
            updatedShapes = updateAtId(updatedShapes, {
              ...shape,
              points: rotatedPoints,
            });
          });
          return updatedShapes;
        }

        // Regular rotation
        let updatedShapes = [...currentShapes];
        selectedShapes.forEach((shape) => {
          updatedShapes = updateAtId(updatedShapes, {
            ...shape,
            points: rotatePoints(
              shape.points,
              isRightArrowPressed ? 0.1 : -0.1
            ),
          });
        });
        return updatedShapes;
      });
      return;
    }

    // Scaling with Shift + Up/Down
    if (isShiftPressed && (isUpArrowPressed || isDownArrowPressed)) {
      setShapes((currentShapes) => {
        const selectedShapes = currentShapes.filter((s) =>
          activeShapes.includes(s.id)
        );

        let updatedShapes = [...currentShapes];
        selectedShapes.forEach((shape) => {
          updatedShapes = updateAtId(updatedShapes, {
            ...shape,
            points: scalePolygon(shape.points, isUpArrowPressed ? 1.01 : 0.99),
          });
        });
        return updatedShapes;
      });
      return;
    }

    // Regular movement (no Shift)
    if (!isShiftPressed) {
      setShapes((currentShapes) => {
        const relevantShapes = currentShapes.filter((s) =>
          activeShapes.includes(s.id)
        );

        let updatedShapes = [...currentShapes];
        relevantShapes.forEach((shape) => {
          const dx = isRightArrowPressed ? 1 : isLeftArrowPressed ? -1 : 0;
          const dy = isDownArrowPressed ? 1 : isUpArrowPressed ? -1 : 0;
          updatedShapes = updateAtId(updatedShapes, {
            ...shape,
            points: shape.points.map((point) => ({
              x: point.x + dx * pixelsToFeetRatio,
              y: point.y + dy * pixelsToFeetRatio,
            })),
          });
        });
        return updatedShapes;
      });
    }
  }, [
    setShapes,
    activeShapes,
    pixelsToFeetRatio,
    isShiftPressed,
    isRightArrowPressed,
    isLeftArrowPressed,
    isUpArrowPressed,
    isDownArrowPressed,
  ]);

  useEffect(() => {
    // Don't set up interval if no keys are pressed
    const anyKeyPressed =
      isLeftArrowPressed ||
      isRightArrowPressed ||
      isDownArrowPressed ||
      isUpArrowPressed;

    if (!anyKeyPressed || activeShapes.length === 0) {
      return;
    }

    // Run immediately on first key press
    performMovement();

    // Set up interval for continuous movement while key is held (50ms = 20 updates/sec)
    const interval = setInterval(performMovement, 5);

    // Cleanup interval when keys are released or component unmounts
    return () => clearInterval(interval);
  }, [
    performMovement,
    activeShapes,
    isLeftArrowPressed,
    isRightArrowPressed,
    isDownArrowPressed,
    isUpArrowPressed,
  ]);

  useHotkeys(
    "ctrl+d",
    () => {
      duplicateShapes();
    },
    [activeShapes, shapes]
  );

  useHotkeys(
    ["meta+z", "ctrl+z"],
    () => {
      undo();
    },
    [undo]
  );
};
